import { ClassProvider, LOCALE_ID, Type, ValueProvider } from '@angular/core';
import { MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from '@angular/material/form-field';
import {
  MAT_ICON_DEFAULT_OPTIONS,
  MatIconDefaultOptions,
} from '@angular/material/icon';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarConfig,
} from '@angular/material/snack-bar';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
} from '@angular/material/tooltip';
import { TitleStrategy } from '@angular/router';
import { Locale } from 'date-fns';
import { AppTitleStrategy } from '../services/app-title-strategy';

/**
 * A provider function to configure MatFormField
 * @param options Options to configure MatFormField
 * @returns A provider object
 */
export const provideMatFormFieldDefaultOptions = (
  options: MatFormFieldDefaultOptions
): ValueProvider => ({
  provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
  useValue: options,
});

/**
 * A provider function to configure MatIcon
 * @param options Options to configure MatIcon
 * @returns A provider object
 */
export const provideMatIconDefaultOptions = (
  options: MatIconDefaultOptions
): ValueProvider => ({
  provide: MAT_ICON_DEFAULT_OPTIONS,
  useValue: options,
});

/**
 * A provider function to configure MatSnackBar
 * @param options Options to configure MatSnackBar
 * @returns A provider object
 */
export const provideMatSnackBarDefaultOptions = (
  options: MatSnackBarConfig
): ValueProvider => ({
  provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
  useValue: options,
});

/**
 * A provider function to configure MatTooltip
 * @param options Options to configure MatTooltip
 * @returns A provider object
 */
export const provideMatTooltipOptions = (
  options: Partial<MatTooltipDefaultOptions>
) => ({
  provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
  useValue: {
    ...MAT_TOOLTIP_DEFAULT_OPTIONS,
    ...options,
  },
});

/**
 * A provider function to configure LOCALE_ID
 * @param locale The language code to use
 * @returns A provider object
 */
export const provideLocaleId = (locale: string): ValueProvider => ({
  provide: LOCALE_ID,
  useValue: locale,
});

/**
 * A provider function to configure MAT_DATE_FORMATS
 * to use MAT_DATE_FNS_FORMATS
 * @returns A provider object
 */
export const provideMatDateFnsFormats = (): ValueProvider => ({
  provide: MAT_DATE_FORMATS,
  useValue: MAT_DATE_FNS_FORMATS,
});

/**
 * A provider function to configure MAT_DATE_LOCALE
 * @param locale The language code or a locale object to use
 * @returns A provider object
 */
export const provideMatDateLocale = (locale: string | Locale): ValueProvider => ({
  provide: MAT_DATE_LOCALE,
  useValue: locale,
});

/**
 * A provider function to configure DateAdapter
 * @param adapter The adapter to use
 * @returns A provider object
 */
export const provideMatDateAdapter = (
  adapter: Type<DateAdapter<Date>>
): ClassProvider => ({
  provide: DateAdapter,
  useClass: adapter,
});

/**
 * A provider function to configure TitleStrategy
 * @returns A provider object
 */
export const provideAppTitleStrategy = () => ({
  provide: TitleStrategy,
  useClass: AppTitleStrategy,
});
